import type { UserMetricsSchema } from "@/utils/userInsights";

// user-insights task_type keys (not lebron task types). COVERAGE_BETA is a
// COVERAGE_REVIEW submission made in the Coverage Beta UI.
export const OOS_CONFIRMATION_TASK_TYPE = "OUT_OF_STOCK_CONFIRMATION";
export const COVERAGE_BETA_TASK_TYPE = "COVERAGE_BETA";

/** Time spent labeling: tasks × average (capped) task time, in seconds. */
function labelingSeconds(metrics: UserMetricsSchema | undefined): number {
  if (!metrics) return 0;
  return metrics.total_tasks_completed * metrics.avg_task_time;
}

/**
 * The task type a profile opens on: whichever of OOS Confirmation and Coverage
 * Beta the labeler spent more time on. Compares time rather than task counts
 * because a Coverage Beta task takes far longer than an OOS one. A tie,
 * including no work at all, keeps OOS, the long-standing default.
 */
export function pickDefaultTaskType(
  oos: UserMetricsSchema | undefined,
  beta: UserMetricsSchema | undefined,
): string {
  return labelingSeconds(beta) > labelingSeconds(oos)
    ? COVERAGE_BETA_TASK_TYPE
    : OOS_CONFIRMATION_TASK_TYPE;
}

/** Boxes labeled per hour of task time, from the average seconds per box. */
export function boxesPerHour(avgBoxTime: number | undefined): number {
  if (!avgBoxTime) return 0;
  return 3600 / avgBoxTime;
}

/**
 * Boxes labeled per shift: user-insights' avg_boxes_per_shift. Where that's
 * missing or 0 (user-insights before it shipped, or stored metrics not yet
 * recomputed), the shift count is recovered as tasks / (tasks per shift).
 */
export function boxesPerShift(
  metrics: Pick<
    UserMetricsSchema,
    | "avg_boxes_per_shift"
    | "boxes_completed"
    | "total_tasks_completed"
    | "avg_tasks_per_shift"
  >,
): number {
  if (metrics.avg_boxes_per_shift) return metrics.avg_boxes_per_shift;
  if (!metrics.total_tasks_completed || !metrics.avg_tasks_per_shift) return 0;
  const shifts = metrics.total_tasks_completed / metrics.avg_tasks_per_shift;
  return (metrics.boxes_completed ?? 0) / shifts;
}
